// @flow strict-local

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import * as api from 'src/utils/api-no-store';

import useSchedulerEvent from './useSchedulerEvent';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import logger from 'src/utils/logger';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  SubTitleLarge,
  BodySmall,
  BodySmallBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Container, NotFoundPage, BookingBody} from './common.jsx';
import {useIsMobile} from '../useIsMobile';
import classify from '@spaced-out/ui-design-system/lib/utils/classify';
import {isLanguageSupported} from 'src/utils/language-support';
import {getLinkOwner, getLinkOwnerLanguage} from './utils';

import css from '../booking.css';


export const RsvpApp = ({
  eventId,
  timezone,
}: {
  eventId: string,
  timezone: string,
}): React.Node => {
  const isMobile = useIsMobile();
  const {labelI18nInstance} = useI18n();
  const {t, makeKey} = useLabelTranslation();
  const eventDataResource = useSchedulerEvent(eventId);
  const [rsvpStatus, setRsvpStatus] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [linkOwnerLanguage, setLinkOwnerLanguage] = React.useState(
    labelI18nInstance.language,
  );

  const url = new URL(window.location);
  const params = Object.fromEntries(url.searchParams.entries());
  const attendeeId = params.aid;

  const handleRsvp = async (rsvpResponse) => {
    setSubmitting(true);
    try {
      await api.post(
        `scheduler/events/${eventId}/participants/${attendeeId}/rsvp`,
        {
          status: rsvpResponse,
        },
      );
      setRsvpStatus(rsvpResponse);
    } catch (error) {
      logger.error('RSVP failed', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveToCalendar = async () => {
    try {
      const response = await api.post(
        `scheduler/events/${eventId}/ics-file`,
        {},
        {
          responseType: 'blob',
        },
      );

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title || 'event'}.ics`);
      document.body?.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Save to calendar failed:', error);
    }
  };

  React.useEffect(() => {
    const updateLanguage = async () => {
      const owner = await getLinkOwner(eventId, params);

      if (!eventDataResource.result) {
        return;
      }

      const userLanguage = getLinkOwnerLanguage({
        linkOwner: owner,
        userId: attendeeId,
        eventData: eventDataResource.result,
      });

      if (
        userLanguage &&
        isLanguageSupported(userLanguage) &&
        userLanguage !== linkOwnerLanguage
      ) {
        setLinkOwnerLanguage(userLanguage);
      }
    };

    updateLanguage();
  }, [eventDataResource.result]);

  if (eventDataResource.isLoading) {
    return null;
  }

  const {brand_settings, title, event_start} = eventDataResource.result || {};
  const meetingDate = event_start ? parseISO(event_start) : new Date();

  const meetingDateDay = format(meetingDate, 'EEEE');
  const meetingDateMonth = format(meetingDate, 'MMMM');
  const meetingDateDayOfMonth = format(meetingDate, 'do');

  return (
    <Container
      brand={brand_settings}
      persistLanguage={false}
      language={linkOwnerLanguage}
    >
      <BookingBody
        className={classify(
          {
            [css.mobileOnly]: isMobile,
            [css.desktopOnly]: !isMobile,
          },
          css.rsvpBox,
        )}
      >
        {eventDataResource.error ? (
          <NotFoundPage />
        ) : (
          <Card classNames={{wrapper: css.rsvpCard}}>
            <div className={css.rsvpDetailsContainer}>
              <SemanticIcon
                name="calendar"
                semantic="information"
                size="large"
                type="solid"
              />

              <SubTitleLarge>{title}</SubTitleLarge>
              <div className={css.meetingDateContainer}>
                <BodySmall>
                  {t('Meeting_scheduled_for', 'Meeting scheduled for')}
                </BodySmall>

                <div className={css.meetingDate}>
                  <BodySmallBold>
                    {`${t(
                      makeKey(meetingDateDay),
                      meetingDateDay,
                    )}, ${meetingDateDayOfMonth} ${t(
                      makeKey(meetingDateMonth),
                      meetingDateMonth,
                    )}`}
                  </BodySmallBold>
                  <BodySmallBold>
                    {format(meetingDate, 'h:mm a')} {timezone}
                  </BodySmallBold>
                </div>
              </div>
            </div>

            <CardFooter>
              <div className={css.actionButtons}>
                <SimpleButtonDropdown
                  classNames={{
                    buttonWrapper: css.rsvpButton,
                    buttonIcon: css.buttonIcon,
                  }}
                  onOptionSelect={(option) => handleRsvp(option.key)}
                  options={[
                    {
                      key: 'yes',
                      label: t('Accept', 'Accept'),
                      iconLeft: 'check',
                    },
                    {
                      key: 'no',
                      label: t('Decline', 'Decline'),
                      iconLeft: 'close',
                    },
                  ]}
                  disabled={submitting}
                  type="primary"
                  selectedKeys={[rsvpStatus]}
                  iconLeftName={
                    rsvpStatus === 'yes'
                      ? 'check'
                      : rsvpStatus === 'no'
                      ? 'close'
                      : ''
                  }
                >
                  {rsvpStatus === 'yes'
                    ? t('Accepted', 'Accepted')
                    : rsvpStatus === 'no'
                    ? t('Declined', 'Declined')
                    : t('RSVP', 'RSVP')}
                </SimpleButtonDropdown>

                <Button
                  type="tertiary"
                  onClick={handleSaveToCalendar}
                  size="medium"
                >
                  {t('Save_Invite_to_Calendar', 'Save Invite to Calendar')}
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </BookingBody>
    </Container>
  );
};
